import { useState, useEffect } from 'react'
import { supabase, supabaseHelpers, friendshipHelpers, notificationHelpers, feedbackHelpers, userInterestsHelpers, backgroundCleanup, setSupabaseAuth, clearSupabaseAuth } from '../config/supabase'

export const useSupabaseIntegration = (user) => {
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Effect to handle user profile when user changes
  useEffect(() => {
    if (user && user.sub) { // Google JWT uses 'sub' as the unique identifier
      handleUserProfile(user)
      // Set Supabase authentication context for real-time subscriptions
      setSupabaseAuth(user.sub)
    } else {
      setProfile(null)
      // Clear Supabase authentication context
      clearSupabaseAuth()
    }
  }, [user])

  const handleUserProfile = async (userData) => {
    try {
      setLoading(true)
      setError(null)

      // Use optimized login handler for better performance
      const { data: profile, error: profileError, isNewUser } = await supabaseHelpers.handleGoogleLogin(
        userData.sub,
        userData.email,
        userData.name,
        userData.picture,
        userData.given_name,
        userData.family_name
      )
      
      if (profileError) {
        console.error('Error handling user profile:', profileError)
        setError('Failed to load user profile')
      } else {
        setProfile(profile)
        
        // Optional: You can use isNewUser flag for different UI flows
        if (isNewUser) {
          console.log('New user registered:', profile)
          // Could trigger onboarding flow, welcome message, etc.
        } else {
          console.log('Existing user logged in:', profile)
        }
      }
    } catch (err) {
      console.error('Error handling user profile:', err)
      setError('Failed to load user profile')
    } finally {
      setLoading(false)
    }
  }

  const updateUserProfile = async (updates) => {
    if (!user || !user.sub) return { data: null, error: 'No user logged in' }

    try {
      const { data, error } = await supabaseHelpers.updateProfile(user.sub, updates)
      if (data) {
        setProfile(data)
        // Log profile update
        await supabaseHelpers.logEvent('profile_updated', {
          user_id: user.sub,
          email: user.email,
          updated_fields: Object.keys(updates),
          update_time: new Date().toISOString()
        })
      }
      return { data, error }
    } catch (err) {
      console.error('Error updating profile:', err)
      return { data: null, error: err }
    }
  }

  const logUserEvent = async (event, details = {}) => {
    if (!user || !user.sub) return { data: null, error: 'No user logged in' }

    try {
      const eventData = {
        user_id: user.sub,
        email: user.email,
        name: user.name,
        ...details,
        timestamp: new Date().toISOString()
      }
      return await supabaseHelpers.logEvent(event, eventData)
    } catch (err) {
      console.error('Error logging event:', err)
      return { data: null, error: err }
    }
  }

  const getAllProfiles = async () => {
    try {
      return await supabaseHelpers.getAllProfiles()
    } catch (err) {
      console.error('Error fetching all profiles:', err)
      return { data: null, error: err }
    }
  }

  const getLogs = async (limit = 100) => {
    try {
      return await supabaseHelpers.getLogs(limit)
    } catch (err) {
      console.error('Error fetching logs:', err)
      return { data: null, error: err }
    }
  }

  // Login Analytics Functions
  const getLoginAnalytics = async (days = 30) => {
    try {
      return await supabaseHelpers.getLoginAnalytics(days)
    } catch (err) {
      console.error('Error fetching login analytics:', err)
      return { data: [], error: err }
    }
  }

  const getActiveUsers = async (days = 7) => {
    try {
      return await supabaseHelpers.getActiveUsers(days)
    } catch (err) {
      console.error('Error fetching active users:', err)
      return { data: [], error: err }
    }
  }

  const getUserLoginStats = async (userId) => {
    try {
      return await supabaseHelpers.getUserLoginStats(userId)
    } catch (err) {
      console.error('Error fetching user login stats:', err)
      return { data: null, error: err }
    }
  }

  const getInactiveUsers = async (days = 30) => {
    try {
      return await supabaseHelpers.getInactiveUsers(days)
    } catch (err) {
      console.error('Error fetching inactive users:', err)
      return { data: [], error: err }
    }
  }

  // Friendship Functions
  const sendFriendRequest = async (friendId) => {
    if (!user || !user.sub) return { data: null, error: 'No user logged in' }
    try {
      return await friendshipHelpers.sendFriendRequest(user.sub, friendId)
    } catch (err) {
      console.error('Error sending friend request:', err)
      return { data: null, error: err }
    }
  }

  const acceptFriendRequest = async (requesterId) => {
    if (!user || !user.sub) return { data: null, error: 'No user logged in' }
    try {
      return await friendshipHelpers.acceptFriendRequest(requesterId, user.sub)
    } catch (err) {
      console.error('Error accepting friend request:', err)
      return { data: null, error: err }
    }
  }

  const rejectFriendRequest = async (requesterId) => {
    if (!user || !user.sub) return { data: null, error: 'No user logged in' }
    try {
      return await friendshipHelpers.rejectFriendRequest(requesterId, user.sub)
    } catch (err) {
      console.error('Error rejecting friend request:', err)
      return { data: null, error: err }
    }
  }

  const getFriends = async () => {
    if (!user || !user.sub) return { data: [], error: 'No user logged in' }
    try {
      return await friendshipHelpers.getFriends(user.sub)
    } catch (err) {
      console.error('Error fetching friends:', err)
      return { data: [], error: err }
    }
  }

  const getPendingRequests = async () => {
    if (!user || !user.sub) return { data: [], error: 'No user logged in' }
    try {
      return await friendshipHelpers.getPendingRequests(user.sub)
    } catch (err) {
      console.error('Error fetching pending requests:', err)
      return { data: [], error: err }
    }
  }

  const getFriendshipStatus = async (otherUserId) => {
    if (!user || !user.sub) return { data: null, error: 'No user logged in' }
    try {
      return await friendshipHelpers.getFriendshipStatus(user.sub, otherUserId)
    } catch (err) {
      console.error('Error checking friendship status:', err)
      return { data: null, error: err }
    }
  }

  const removeFriend = async (friendId) => {
    if (!user || !user.sub) return { data: null, error: 'No user logged in' }
    try {
      return await friendshipHelpers.removeFriend(user.sub, friendId)
    } catch (err) {
      console.error('Error removing friend:', err)
      return { data: null, error: err }
    }
  }

  // Notification Functions
  const getUserNotifications = async (limit = 50) => {
    if (!user || !user.sub) return { data: [], error: 'No user logged in' }
    try {
      return await notificationHelpers.getUserNotifications(user.sub, limit)
    } catch (err) {
      console.error('Error fetching notifications:', err)
      return { data: [], error: err }
    }
  }

  const getUnreadNotificationCount = async () => {
    if (!user || !user.sub) return { data: 0, error: 'No user logged in' }
    try {
      return await notificationHelpers.getUnreadNotificationCount(user.sub)
    } catch (err) {
      console.error('Error fetching unread notification count:', err)
      return { data: 0, error: err }
    }
  }

  const markNotificationRead = async (notificationId) => {
    try {
      return await notificationHelpers.markNotificationRead(notificationId)
    } catch (err) {
      console.error('Error marking notification read:', err)
      return { data: null, error: err }
    }
  }

  const markAllNotificationsRead = async () => {
    if (!user || !user.sub) return { data: [], error: 'No user logged in' }
    try {
      return await notificationHelpers.markAllNotificationsRead(user.sub)
    } catch (err) {
      console.error('Error marking all notifications read:', err)
      return { data: [], error: err }
    }
  }

  const deleteNotification = async (notificationId) => {
    try {
      return await notificationHelpers.deleteNotification(notificationId)
    } catch (err) {
      console.error('Error deleting notification:', err)
      return { data: null, error: err }
    }
  }

  return {
    // Supabase client for direct access
    supabase,
    // Supabase profile data
    profile,
    // States
    loading,
    error,
    // Helper functions
    updateUserProfile,
    logUserEvent,
    getAllProfiles,
    getLogs,
    // Login analytics functions
    getLoginAnalytics,
    getActiveUsers,
    getUserLoginStats,
    getInactiveUsers,
    // Friendship functions
    sendFriendRequest,
    acceptFriendRequest,
    rejectFriendRequest,
    getFriends,
    getPendingRequests,
    getFriendshipStatus,
    removeFriend,
    // Notification functions
    getUserNotifications,
    getUnreadNotificationCount,
    markNotificationRead,
    markAllNotificationsRead,
    deleteNotification,
    // Raw Supabase helpers for additional operations
    supabaseHelpers: {
      ...supabaseHelpers,
      // Friendship functions
      getFriendshipById: friendshipHelpers.getFriendshipById,
      // Feedback functions
      submitFeedback: feedbackHelpers.submitFeedback,
      getUserFeedback: feedbackHelpers.getUserFeedback,
      getAllFeedback: feedbackHelpers.getAllFeedback,
      updateFeedbackStatus: feedbackHelpers.updateFeedbackStatus,
      getFeedbackStats: feedbackHelpers.getFeedbackStats,
      assignFeedback: feedbackHelpers.assignFeedback,
      addResolutionNotes: feedbackHelpers.addResolutionNotes,
      // User interests functions
      addUserInterest: userInterestsHelpers.addUserInterest,
      removeUserInterest: userInterestsHelpers.removeUserInterest,
      getUserInterests: userInterestsHelpers.getUserInterests,
      getVisibleInterestsForPlace: userInterestsHelpers.getVisibleInterestsForPlace,
      getAllVisibleInterests: userInterestsHelpers.getAllVisibleInterests,
      getPlaceInterestCounts: userInterestsHelpers.getPlaceInterestCounts,
      debugPlaceInterests: userInterestsHelpers.debugPlaceInterests,
      debugFriendshipStatus: userInterestsHelpers.debugFriendshipStatus,
      debugMakeFriends: userInterestsHelpers.debugMakeFriends,
      cleanupExpiredInterests: userInterestsHelpers.cleanupExpiredInterests,
      // Privacy mode functions
      getUserPrivacyMode: userInterestsHelpers.getUserPrivacyMode,
      updateUserPrivacyMode: userInterestsHelpers.updateUserPrivacyMode,
      // Background cleanup functions
      runCleanup: backgroundCleanup.runCleanup,
      startPeriodicCleanup: backgroundCleanup.startPeriodicCleanup,
      stopPeriodicCleanup: backgroundCleanup.stopPeriodicCleanup,
      // Onboarding functions
      getUnansweredOnboardingQuestions: userInterestsHelpers.getUnansweredOnboardingQuestionsUnified,
      saveProfileResponse: userInterestsHelpers.saveProfileResponse,
      getUserProfileResponses: userInterestsHelpers.getUserProfileResponses,
      updateProfileResponse: userInterestsHelpers.updateProfileResponse
    }
  }
} 